import { useState } from "react";
import { FadeIn } from "@/components/FadeIn";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Phone, MapPin, Instagram, Mail, Clock, Loader2, CheckCircle2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const empty = { name: "", phone: "", email: "", vehicle: "", message: "" };

export const ContactSection = ({ content }) => {
  const { t, tc } = useLang();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) { toast.error(t("contact.needFields")); return; }
    setLoading(true);
    try {
      await api.post("/contacts", form);
      toast.success(t("contact.successText"));
      setDone(true);
      setForm(empty);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const mapsQuery = encodeURIComponent(content.address || "Dorsten");

  return (
    <section id="kontakt" data-testid="contact-section" className="py-24 md:py-32 bg-slate-50 border-t border-slate-200">
      <div className="container-x">
        <FadeIn className="max-w-2xl mb-14">
          <span className="overline">{t("contact.overline")}</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mt-3 mb-4">{t("contact.title")}</h2>
          <p className="text-slate-500 md:text-lg">{t("contact.subtitle")}</p>
        </FadeIn>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          <FadeIn>
            <div className="card-light p-7 sm:p-9">
              {done ? (
                <div data-testid="contact-success" className="text-center py-10">
                  <CheckCircle2 className="w-16 h-16 text-blue-600 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">{t("contact.successTitle")}</h3>
                  <p className="text-slate-500 mb-6">{t("contact.successText")}</p>
                  <button onClick={() => setDone(false)} className="btn-ghost">{t("contact.another")}</button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form" className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <input data-testid="contact-name" className="input-light" placeholder={t("contact.name")} value={form.name} onChange={set("name")} />
                    <input data-testid="contact-phone" className="input-light" placeholder={t("contact.phone")} value={form.phone} onChange={set("phone")} />
                  </div>
                  <input data-testid="contact-email" type="email" className="input-light" placeholder={t("contact.email")} value={form.email} onChange={set("email")} />
                  <input data-testid="contact-vehicle" className="input-light" placeholder={t("contact.vehicle")} value={form.vehicle} onChange={set("vehicle")} />
                  <textarea data-testid="contact-message" rows="4" className="input-light resize-none" placeholder={t("contact.message")} value={form.message} onChange={set("message")} />
                  <button data-testid="contact-submit" type="submit" disabled={loading} className="btn-blue w-full">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("contact.submit")}
                  </button>
                </form>
              )}
            </div>
          </FadeIn>

          <FadeIn delay={0.15}>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <a href={`tel:${content.phone?.replace(/\s/g, "")}`} data-testid="contact-info-phone" className="card-light p-5 flex items-center gap-3">
                  <span className="w-11 h-11 rounded-full bg-blue-50 flex items-center justify-center"><Phone className="w-5 h-5 text-blue-600" /></span>
                  <div><div className="text-xs text-slate-400">{t("contact.phoneLabel")}</div><div className="text-slate-900 text-sm font-medium">{content.phone}</div></div>
                </a>
                <a href={content.instagram} target="_blank" rel="noreferrer" data-testid="contact-info-instagram" className="card-light p-5 flex items-center gap-3">
                  <span className="w-11 h-11 rounded-full bg-blue-50 flex items-center justify-center"><Instagram className="w-5 h-5 text-blue-600" /></span>
                  <div><div className="text-xs text-slate-400">Instagram</div><div className="text-slate-900 text-sm font-medium">{content.instagram_handle}</div></div>
                </a>
                <div className="card-light p-5 flex items-center gap-3">
                  <span className="w-11 h-11 rounded-full bg-blue-50 flex items-center justify-center"><MapPin className="w-5 h-5 text-blue-600" /></span>
                  <div><div className="text-xs text-slate-400">{t("contact.addressLabel")}</div><div className="text-slate-900 text-sm font-medium">{content.address}</div></div>
                </div>
                <a href={`mailto:${content.email}`} data-testid="contact-info-email" className="card-light p-5 flex items-center gap-3">
                  <span className="w-11 h-11 rounded-full bg-blue-50 flex items-center justify-center"><Mail className="w-5 h-5 text-blue-600" /></span>
                  <div className="min-w-0"><div className="text-xs text-slate-400">{t("contact.emailLabel")}</div><div className="text-slate-900 text-sm font-medium truncate">{content.email}</div></div>
                </a>
              </div>

              <div className="card-light p-6">
                <div className="flex items-center gap-2 mb-4 text-slate-900 font-semibold"><Clock className="w-5 h-5 text-blue-600" /> {t("contact.hours")}</div>
                <div className="space-y-2">
                  {(content.opening_hours || []).map((o, i) => (
                    <div key={i} className="flex justify-between text-sm border-b border-slate-100 pb-2 last:border-0">
                      <span className="text-slate-500">{tc(o.day)}</span>
                      <span className={o.hours === "Geschlossen" ? "text-blue-600 font-medium" : "text-slate-900"}>{tc(o.hours)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-3xl overflow-hidden border border-slate-200 h-64">
                <iframe
                  title="Standort"
                  data-testid="google-map"
                  width="100%" height="100%" loading="lazy"
                  style={{ border: 0 }}
                  src={`https://maps.google.com/maps?q=${mapsQuery}&output=embed`}
                />
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
};
